/**
 * Budgira App - Main Application Component
 * 
 * Sets up routing and authentication guards.
 * 
 * @validates Requirements 18.2 - React 18 with TypeScript
 * @validates Requirements 3.6 - Route protection
 */

import { useEffect, useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useAccountStore } from './store/accountStore';
import { MainLayout, AuthLayout, PublicLayout } from './components/layout';
import {
  LoginPage,
  RegisterPage,
  TwoFactorPage,
  SetPasswordPage,
  VerifyEmailPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ConfirmEmailChangePage,
  DashboardPage,
  AccountsPage,
  AccountFormPage,
  OperationsPage,
  OperationFormPage,
  ExportPage,
  ImportPage,
  SchedulersPage,
  SchedulerExportPage,
  SchedulerFormPage,
  CategoriesPage,
  PaymentMethodsPage,
  CounterpartiesPage,
  UserSettingsPage,
  PremiumPage,
  StatisticsPage,
  ProjectsPage,
  ProjectFormPage,
  ReconciliationPage,
  ReconciliationSessionPage,
  BilansPage,
  BilanDetailPage,
  BilanComparePage,
} from './pages';
import CheckbooksPage from './pages/CheckbooksPage';
import HomePage from './pages/HomePage';
import { AdminPage } from './pages/admin/AdminPage';
import { AboutPage, ContactPage, PrivacyPage, TermsPage } from './pages/legal';
import { useUserSettingsStore } from './store/userSettingsStore';
import { useOperationStore } from './store/operationStore';
import { UserRole } from '@budgira/shared';

/**
 * Reconciliation Guard - redirects to dashboard if user cannot use reconciliation
 * Uses accountStore to check if user has write access on at least one account
 */
const ReconciliationGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuthStore();
  const { accounts } = useAccountStore();

  // Check if user can use reconciliation
  const canUseReconciliation = useMemo(() => {
    if (!user) return false;
    
    // For secondary users (shared access), check effective role and account permissions FIRST
    // (their own user.role may be 'free' but they inherit effectiveRole from owner)
    if (user.sharedAccess) {
      // Must have premium or admin effective role
      const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
      if (!hasPremiumRole) return false;
      
      // If accounts not loaded yet, allow access (will be blocked by API if no write access)
      if (accounts.length === 0) return true;
      
      // Check if at least one account has write or owner permission
      const hasWriteAccess = accounts.some(
        (account) => account.permission === 'write' || account.permission === 'owner'
      );
      return hasWriteAccess;
    }
    
    // For primary users (no sharedAccess), check their own role
    // Free users cannot use reconciliation
    if (user.role === UserRole.FREE) return false;
    
    // Premium and admin primary users can always use it
    return true;
  }, [user, accounts]);

  if (isLoading) {
    return null;
  }

  if (!user) {
    return null;
  }

  if (!canUseReconciliation) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * Protected Route wrapper - redirects to login if not authenticated
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();
  const [isHydrated, setIsHydrated] = useState(useAuthStore.persist.hasHydrated());

  useEffect(() => {
    const unsubFinishHydration = useAuthStore.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });
    
    // If already hydrated, update state
    if (useAuthStore.persist.hasHydrated()) {
      setIsHydrated(true);
    }
    
    return () => {
      unsubFinishHydration();
    };
  }, []);

  // Show loading while store is hydrating, auth is being checked, or user is being loaded
  if (!isHydrated || isLoading || (isAuthenticated && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

/**
 * Admin Protected Route wrapper - redirects to dashboard if not admin
 * @validates Requirements 4.1 - Admin-only template management
 */
const AdminProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, isAdmin } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  if (!isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * Permission Guard - redirects to dashboard if permission check fails
 * Used inside pages that require specific permissions
 */
const PermissionGuard = ({ 
  children, 
  hasPermission 
}: { 
  children: React.ReactNode;
  hasPermission: () => boolean;
}) => {
  const { user, isLoading } = useAuthStore();

  // Wait for auth to complete loading
  if (isLoading) {
    return null; // ProtectedRoute already shows loading spinner
  }

  // Wait for user to be loaded before checking permissions
  if (!user) {
    return null;
  }

  if (!hasPermission()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * Public Route wrapper - redirects to dashboard if already authenticated
 */
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, requiresTwoFactor } = useAuthStore();
  const location = useLocation();

  // Allow access to 2FA page if 2FA is required
  if (requiresTwoFactor && location.pathname === '/auth/2fa') {
    return <>{children}</>;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * Legal Pages Layout - chooses MainLayout for authenticated users, PublicLayout otherwise
 * This allows authenticated users to have the full navigation sidebar on legal pages
 */
const LegalPagesLayout = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [isHydrated, setIsHydrated] = useState(useAuthStore.persist.hasHydrated());

  useEffect(() => {
    const unsubFinishHydration = useAuthStore.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });
    
    if (useAuthStore.persist.hasHydrated()) {
      setIsHydrated(true);
    }
    
    return () => {
      unsubFinishHydration();
    };
  }, []);

  // Show loading while hydrating
  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Use MainLayout for authenticated users, PublicLayout for guests
  return isAuthenticated ? <MainLayout /> : <PublicLayout />;
};

/**
 * App Router Component
 */
const AppRouter = () => {
  const { checkAuth, isAuthenticated, canManageCategories, canManagePaymentMethods, canManageSchedulers } = useAuthStore();
  const { fetchPreferences } = useUserSettingsStore();
  const { initializeFromPreferences } = useOperationStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Load user preferences when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferences().then(() => {
        initializeFromPreferences();
        
        // Apply dark mode theme from preferences
        const { preferences } = useUserSettingsStore.getState();
        if (preferences?.darkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      });
    } else {
      // Remove dark mode when logged out
      document.documentElement.classList.remove('dark');
    }
  }, [isAuthenticated, fetchPreferences, initializeFromPreferences]);

  return (
    <Routes>
      {/* Public auth routes */}
      <Route
        element={
          <PublicRoute>
            <AuthLayout />
          </PublicRoute>
        }
      >
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/auth/2fa" element={<TwoFactorPage />} />
      </Route>

      {/* Invitation route - always public */}
      <Route element={<AuthLayout />}>
        <Route path="/invitation/:token" element={<SetPasswordPage />} />
        <Route path="/set-password/:token" element={<SetPasswordPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      </Route>
      
      {/* Email verification - always public, standalone page */}
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      
      {/* Email change confirmation - always public, standalone page */}
      <Route path="/confirm-email-change/:token" element={<ConfirmEmailChangePage />} />

      {/* Protected app routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/statistics" element={<StatisticsPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/accounts/new" element={<AccountFormPage />} />
        <Route path="/accounts/:accountId/edit" element={<AccountFormPage />} />
        <Route path="/accounts/:accountId" element={<OperationsPage />} />
        <Route path="/accounts/:accountId/operations" element={<OperationsPage />} />
        <Route path="/accounts/:accountId/operations/new" element={<OperationFormPage />} />
        <Route path="/accounts/:accountId/operations/export" element={<ExportPage />} />
        <Route path="/accounts/:accountId/operations/import" element={<ImportPage />} />
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/operations/:operationId/edit" element={<OperationFormPage />} />
        <Route path="/settings" element={<UserSettingsPage />} />
        <Route path="/admin" element={<PlaceholderPage title="Admin" />} />
        <Route path="/premium" element={<PremiumPage />} />
        
        {/* Project routes */}
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/new" element={<ProjectFormPage />} />
        <Route path="/projects/:projectId/edit" element={<ProjectFormPage />} />
        
        {/* Checkbooks route */}
        <Route path="/checkbooks" element={<CheckbooksPage />} />
        
        {/* Reconciliation routes - Premium only, requires write access */}
        <Route path="/reconciliation" element={
          <ReconciliationGuard>
            <ReconciliationPage />
          </ReconciliationGuard>
        } />
        <Route path="/reconciliation/:sessionId" element={
          <ReconciliationGuard>
            <ReconciliationSessionPage />
          </ReconciliationGuard>
        } />
        
        {/* Bilans routes (accounting statements) */}
        <Route path="/bilans" element={<BilansPage />} />
        <Route path="/bilans/:id" element={<BilanDetailPage />} />
        <Route path="/bilans/compare" element={<BilanComparePage />} />
        
        {/* Permission-protected routes (inside same layout) */}
        <Route path="/schedulers" element={
          <PermissionGuard hasPermission={canManageSchedulers}>
            <SchedulersPage />
          </PermissionGuard>
        } />
        <Route path="/schedulers/:schedulerId/edit" element={
          <PermissionGuard hasPermission={canManageSchedulers}>
            <SchedulerFormPage />
          </PermissionGuard>
        } />
        <Route path="/accounts/:accountId/schedulers" element={
          <PermissionGuard hasPermission={canManageSchedulers}>
            <SchedulersPage />
          </PermissionGuard>
        } />
        <Route path="/accounts/:accountId/schedulers/new" element={
          <PermissionGuard hasPermission={canManageSchedulers}>
            <SchedulerFormPage />
          </PermissionGuard>
        } />
        <Route path="/accounts/:accountId/schedulers/export" element={
          <PermissionGuard hasPermission={canManageSchedulers}>
            <SchedulerExportPage />
          </PermissionGuard>
        } />
        <Route path="/categories" element={
          <PermissionGuard hasPermission={canManageCategories}>
            <CategoriesPage />
          </PermissionGuard>
        } />
        <Route path="/payment-methods" element={
          <PermissionGuard hasPermission={canManagePaymentMethods}>
            <PaymentMethodsPage />
          </PermissionGuard>
        } />
        <Route path="/counterparties" element={
          <PermissionGuard hasPermission={canManageCategories}>
            <CounterpartiesPage />
          </PermissionGuard>
        } />
      </Route>

      {/* Admin-only routes */}
      <Route
        element={
          <AdminProtectedRoute>
            <MainLayout />
          </AdminProtectedRoute>
        }
      >
        <Route path="/admin/templates" element={<AdminPage />} />
      </Route>

      {/* Public legal pages - use MainLayout when authenticated, PublicLayout otherwise */}
      <Route element={<LegalPagesLayout />}>
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Route>

      {/* Home page - shows landing for guests, redirects to dashboard for authenticated users */}
      <Route path="/" element={<HomeOrDashboard />} />

      {/* Fallback redirects */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/**
 * Home or Dashboard component - shows HomePage for guests, redirects to dashboard for authenticated users
 */
const HomeOrDashboard = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [isHydrated, setIsHydrated] = useState(useAuthStore.persist.hasHydrated());

  useEffect(() => {
    const unsubFinishHydration = useAuthStore.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });
    
    if (useAuthStore.persist.hasHydrated()) {
      setIsHydrated(true);
    }
    
    return () => {
      unsubFinishHydration();
    };
  }, []);

  // Show loading while hydrating
  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Authenticated users go to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Guests see the landing page
  return <HomePage />;
};

/**
 * Placeholder page for routes not yet implemented
 */
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      </div>
      <h2 className="text-lg font-medium text-slate-900 mb-2">Coming soon</h2>
      <p className="text-slate-500">This page is under construction.</p>
    </div>
  </div>
);

/**
 * Main App Component
 */
function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
